import { Button, CheckboxGroup } from '@lmc/ui-kit'
import { Popover } from 'antd'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { StatusFilter, TeamFilters } from '../../utils/teamFilters'
import TeamFiltersButton from './TeamFiltersButton'

type TeamFiltersPopoverProps = {
  // Currently-applied filters (the source of truth lives in the screen).
  filters: TeamFilters
  onApply: (filters: TeamFilters) => void
  testId?: string
}

type CheckboxValueType = string | number | boolean

const DIVIDER = '#f0f0f0'

const Content = styled.div`
  width: 288px;
  display: flex;
  flex-direction: column;
`

// Popover header ("Filters"), mirroring the design: #333 / 16px / medium with a
// full-width bottom divider.
const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${DIVIDER};
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`

const SectionTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;
  gap: 8px;

  .ant-checkbox-wrapper {
    margin-left: 0;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid ${DIVIDER};
`

// Single-site Filters surface: a popover anchored to the Filters button. Mirrors
// the design — a "Filters" header, a Status section (role is the tab row), and a
// Cancel / Apply footer. Note the single-site footer inverts the group panel:
// Cancel is the blue text action and Apply filters is the outlined button; and
// the button shows no count indicator. Draft-then-apply (CM-3581).
const TeamFiltersPopover: React.FC<TeamFiltersPopoverProps> = ({
  filters,
  onApply,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tGeneral } = useTranslation(NAMESPACE.GENERAL)
  const [open, setOpen] = useState(false)
  const [draftStatuses, setDraftStatuses] = useState<StatusFilter[]>(
    filters.statuses
  )

  // Re-seed the draft from the applied filters each time the popover opens, so a
  // cancelled edit never lingers into the next open.
  useEffect(() => {
    if (open) {
      setDraftStatuses(filters.statuses)
    }
  }, [open, filters.statuses])

  const statusOptions = [
    { value: 'active', label: t(reportsConstants.ACTIVE) },
    { value: 'inactive', label: t(reportsConstants.INACTIVE) }
  ]

  const handleApply = () => {
    onApply({ statuses: draftStatuses, siteIds: [] })
    setOpen(false)
  }

  // Any non-Apply close — Cancel, outside-click, or trigger toggle — discards the
  // draft (the design's "Cancel/close discards"). Resetting here, not only via the
  // re-seed effect on the next open, stops a dismissed edit from flashing back.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDraftStatuses(filters.statuses)
    }
    setOpen(next)
  }

  const content = (
    <Content data-testid={testId ? `${testId}-popover` : undefined}>
      <Header>{t(reportsConstants.TEAM_LIST_FILTERS)}</Header>
      <Section>
        <SectionTitle>
          {t(reportsConstants.TEAM_LIST_FILTER_STATUS)}
        </SectionTitle>
        <StyledCheckboxGroup
          aria-label={t(reportsConstants.TEAM_LIST_FILTER_STATUS)}
          options={statusOptions}
          value={draftStatuses}
          onChange={(values: CheckboxValueType[]) =>
            setDraftStatuses(values as StatusFilter[])
          }
          data-testid={testId ? `${testId}-status` : undefined}
        />
      </Section>
      <Footer>
        <Button
          variant="text"
          onClick={() => handleOpenChange(false)}
          data-testid={testId ? `${testId}-cancel` : undefined}
        >
          {tGeneral(generalConstants.CANCEL)}
        </Button>
        <Button
          onClick={handleApply}
          data-testid={testId ? `${testId}-apply` : undefined}
        >
          {t(reportsConstants.TEAM_LIST_FILTER_APPLY)}
        </Button>
      </Footer>
    </Content>
  )

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      content={content}
      overlayInnerStyle={{ padding: 0 }}
    >
      {/* Wrapper span gives antd a DOM node to anchor + bind the click trigger
          to, independent of the ui-kit Button's ref handling. */}
      <span>
        <TeamFiltersButton open={open} testId={testId} />
      </span>
    </Popover>
  )
}

export default TeamFiltersPopover
